import {
  BaseError,
  ContractFunctionRevertedError,
  ContractFunctionZeroDataError,
  parseAbi,
  zeroAddress,
  type Abi,
  type Address,
} from 'viem';
import { TOKEN_LOCK_ABI } from './TokenLock.abi';
import { TOKEN_LOCK_FACTORY_ABI } from './TokenLockFactory.abi';
import { ERC20_ABI } from './ERC20.abi';
import type { FactoryAbiVersion } from '@/config/environment';
import { Category, type LockInfo } from '@/hooks/useWeb3';

export interface ContractReader {
  readContract: (args: any) => Promise<any>;
}

/**
 * getLockState() has grown twice and every version answers the SAME four-byte
 * selector, so the deployed shape has to be chosen from configuration rather
 * than discovered. Each tuple below is frozen: it describes a contract that is
 * already on chain and can never change again.
 */
type LegacyLockState = readonly [
  bigint, bigint, Address, bigint, bigint, bigint, bigint, boolean, bigint, bigint, number,
];
/** v9 appended earnedUnclaimed, lockBalance, nextUnlockTime. */
type V9LockState = readonly [...LegacyLockState, bigint, bigint, bigint];
/** v10 appended maxEntitlement, cancelled, excessFunding. */
type FundedLockState = readonly [...V9LockState, bigint, boolean, bigint];

/** Anything readLockState may be handed, from any of the three deployments. */
export type RawLockState = LegacyLockState | V9LockState | FundedLockState;

// The committed v7 deployment predates incremental funding. A newer ABI cannot
// decode its shorter return data, although both versions use the same selector.
const LEGACY_LOCK_STATE_ABI = parseAbi([
  'function getLockState() view returns (uint256, uint256, address, uint256, uint256, uint256, uint256, bool, uint256, uint256, uint8)',
]);

// Today's Base Sepolia factory (v9). Frozen here for exactly the same reason the
// v7 tuple is: TokenLock.abi.ts now describes SEVENTEEN return values, and viem
// throws rather than decoding fourteen words against it. Without this the whole
// testnet roster reads as an error, not as an empty allocation.
export const V9_LOCK_STATE_ABI = parseAbi([
  'function getLockState() view returns (uint256, uint256, address, uint256, uint256, uint256, uint256, bool, uint256, uint256, uint8, uint256, uint256, uint256)',
]);

export const LEGACY_FUNDING_ABI = parseAbi([
  'function calculateTotalTokensNeeded() view returns (uint256)',
]);

// One beneficiary, one lock -- the rule v10 abolished. The selector is gone from
// the regenerated factory ABI, so it is kept here the same way the legacy
// getLockState tuples are: a v7/v9 factory still answers it, and it is the only
// way to find a beneficiary's grant there.
// v7 and v9 expose the UNCAPPED arm-and-catch-up entrypoints. Only v10 takes a
// spend cap, so a bundle that encodes the capped shapes unconditionally cannot
// call either one on any factory currently deployed -- different arity is a
// different four-byte selector, and the call would revert as an unknown
// function. Frozen here beside the other legacy shapes for the same reason.
export const UNCAPPED_INIT_ABI = parseAbi([
  'function initializeAll()',
  'function initializeLock(address)',
]);

export const LEGACY_LOCK_LOOKUP_ABI = parseAbi([
  'function getLockContract(address) view returns (address)',
]);

/**
 * The ABI to decode getLockState() with on a given deployment. Exported because
 * the admin panel batches this call through Multicall3 and therefore has to
 * build the request itself -- but the SELECTION lives here, so there is still
 * exactly one place that knows which shape each deployment returns.
 */
export function lockStateAbiFor(version: FactoryAbiVersion): Abi {
  if (version === 'v7') return LEGACY_LOCK_STATE_ABI;
  if (version === 'v9') return V9_LOCK_STATE_ABI;
  return TOKEN_LOCK_ABI as unknown as Abi;
}

export async function readFactoryOwnership(client: ContractReader, factory: Address) {
  const owner = await client.readContract({
    address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'owner',
  }) as Address;
  try {
    const pendingOwner = await client.readContract({
      address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'pendingOwner',
    }) as Address;
    return { owner, pendingOwner, incrementalFunding: true };
  } catch (error) {
    // Missing selectors on the old factory revert without data. Network and RPC
    // failures must still surface; they do not mean this is a legacy deployment.
    const missing = error instanceof BaseError && error.walk((cause) =>
      cause instanceof ContractFunctionZeroDataError ||
      (cause instanceof ContractFunctionRevertedError &&
        (!cause.raw || cause.raw === '0x') &&
        (!cause.reason || cause.reason === 'execution reverted')),
    );
    if (!missing) throw error;
    return { owner, pendingOwner: zeroAddress, incrementalFunding: false };
  }
}

/**
 * Turn one already-fetched getLockState() tuple into a LockInfo.
 *
 * This is the batch sibling of readLockState and the two share every rule: the
 * positional defaults, the v7 token()/balanceOf() fallback and the
 * availableAmount clamp all live here, so a caller that fetched the raw tuple
 * through Multicall3 cannot end up with a subtly different LockInfo from one
 * that read it directly.
 */
export async function lockStateFromRaw(
  client: ContractReader,
  lockAddress: Address,
  version: FactoryAbiVersion,
  state: RawLockState,
): Promise<LockInfo> {
  const [totalAmount, unlockedAmount, beneficiary, startTime, vestedAmount,
    availableAmount, offboardTime, isInitialized, unlockScheduleAmount,
    unlockCliffTime, category] = state;

  const earnedUnclaimed: bigint = state[11] ?? availableAmount;
  let lockBalance: bigint = state[12] ?? 0n;
  if (version === 'v7') {
    const token = await client.readContract({
      address: lockAddress, abi: TOKEN_LOCK_ABI, functionName: 'token',
    }) as Address;
    lockBalance = await client.readContract({
      address: token, abi: ERC20_ABI, functionName: 'balanceOf', args: [lockAddress],
    });
  }
  return {
    totalAmount, unlockedAmount, beneficiary, startTime, vestedAmount,
    // Legacy claim() pays all earned tokens or reverts; partial payouts are new.
    availableAmount: version !== 'v7' || lockBalance >= availableAmount ? availableAmount : 0n,
    offboardTime, isInitialized, unlockScheduleAmount, unlockCliffTime, category,
    earnedUnclaimed, lockBalance, nextUnlockTime: state[13] ?? 0n,
    // maxEntitlement defaults to totalAmount, NEVER to 0n. On v7 and v9 no lock
    // can be cancelled and there is no ceiling below the allocation other than
    // an off-board freeze, so totalAmount is the honest fallback -- while 0n
    // would make every "still locked" and "remaining" figure on those
    // deployments read zero for the entire roster at once.
    maxEntitlement: state[14] ?? totalAmount,
    cancelled: state[15] ?? false,
    excessFunding: state[16] ?? 0n,
  };
}

/**
 * The honest ceiling on what a lock can still be worth: the most it can ever pay
 * out in total, whatever the ABI version reported.
 *
 * This lives here, beside the `?? totalAmount` default that makes it necessary,
 * rather than in a screen. A v10 factory answers the question itself through
 * maxEntitlement. A v7 or v9 one does not report a ceiling at all, so the decode
 * falls back to totalAmount — which is right for a healthy lock and WRONG for an
 * off-boarded one, whose real ceiling is the vesting curve frozen at the
 * off-board date. That frozen figure is exactly what `vestedAmount` already
 * reads once the freeze has taken effect, so it is recoverable; before the
 * freeze date lands it is not, and totalAmount remains the best available answer.
 *
 * Every consumer must use this rather than reading `maxEntitlement` directly:
 * when only one of them did, the admin panel and the beneficiary's own screen
 * printed different totals for the same off-boarded grant.
 */
export const payableCeiling = (
  lock: Pick<LockInfo, 'cancelled' | 'offboardTime' | 'maxEntitlement' | 'vestedAmount'>,
  nowMs: number,
): bigint => {
  if (lock.cancelled) return BigInt(0);
  const offboardSeconds = Number(lock.offboardTime);
  const frozen = offboardSeconds > 0 && offboardSeconds * 1000 <= nowMs;
  if (frozen && lock.maxEntitlement > lock.vestedAmount) return lock.vestedAmount;
  return lock.maxEntitlement;
};

/**
 * The vested figure to SHOW for a lock.
 *
 * Investors have no vesting cliff of their own: their cliff is the investor
 * unlock cliff, the same amount on the same date. The lock still runs the
 * onboard-anchored vesting curve for both tracks, and an investor's start date
 * is backdated far enough that the curve always runs ahead of the unlock
 * schedule. Shown raw, it reads as a second schedule with a cliff nobody was
 * promised: a grant starting 2024-12-31 "vests" 25% on 2025-12-31, nine months
 * before anything unlocks.
 *
 * So an investor is shown entitledAmount(), min(vesting, unlock) -- the unlock
 * curve whenever vesting is ahead of it, and still the truth when it is not: a
 * start date late enough to push the vesting cliff past the unlock cliff really
 * does hold the grant back, and the screen must not promise what claim() will
 * refuse. Team grants keep their own vesting curve.
 *
 * Display only. payableCeiling and the funding figures keep the raw
 * vestedAmount -- the v7/v9 off-board fallback above depends on it.
 */
export const displayedVestedAmount = (
  lock: Pick<LockInfo, 'category' | 'vestedAmount' | 'unlockScheduleAmount'>,
): bigint => {
  if (lock.category !== Category.INVESTOR) return lock.vestedAmount;
  return lock.vestedAmount < lock.unlockScheduleAmount
    ? lock.vestedAmount
    : lock.unlockScheduleAmount;
};

export async function readLockState(
  client: ContractReader,
  lockAddress: Address,
  version: FactoryAbiVersion,
): Promise<LockInfo> {
  const state = await client.readContract({
    address: lockAddress,
    abi: lockStateAbiFor(version),
    functionName: 'getLockState',
  }) as RawLockState;
  return lockStateFromRaw(client, lockAddress, version, state);
}
